import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfiguratorState } from '../../types';
import {
  CatalogProduct,
  catalogPreviewDimensions,
  catalogProductKind,
  catalogShortDescription,
  filterCatalogProducts,
  stateUpdatesForProduct,
} from '../../utils/catalogProducts';
import {
  NeedChoice,
  NeedsAnswers,
  leadingNeedProduct,
  needsQuestions,
  rankNeedProducts,
} from '../../config/needsFinder';
import PerspectiveViewer from '../UI/PerspectiveViewer';
import { formatDisplayPrice } from '../../utils/priceFormat';

interface KonfiguratorConfig {
  products: string;
  showVisualizer: boolean;
  theme: string;
  compact: boolean;
}

interface NeedsFinderProps {
  updateState: (updates: Partial<ConfiguratorState>) => void;
  config?: KonfiguratorConfig;
  showGross?: boolean;
  answers: NeedsAnswers;
  onAnswersChange: (answers: NeedsAnswers) => void;
  onSkipToCatalog: () => void;
  onChooseProduct: (product: CatalogProduct) => void;
  wizardIndex: number;
  onWizardIndexChange: (index: number) => void;
  onWizardTotalChange: (total: number) => void;
}

function isChoiceSelected<T extends string>(
  answers: NeedsAnswers,
  key: keyof NeedsAnswers,
  id: T,
): boolean {
  return answers[key] === id;
}

export default function NeedsFinder({
  updateState,
  config,
  showGross = true,
  answers,
  onAnswersChange,
  onSkipToCatalog,
  onChooseProduct,
  wizardIndex,
  onWizardIndexChange,
  onWizardTotalChange,
}: NeedsFinderProps) {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const advanceTimer = useRef<number | null>(null);
  const paneHasStepped = useRef(false);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/wp-json/skyvision/v1/products');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Ungültige API-Antwort');
      setProducts(data);
    } catch (err) {
      console.error('Fehler beim Laden der Produkte:', err);
      setError('Produkte konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    paneHasStepped.current = true;
  }, [wizardIndex]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current != null) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const available = useMemo(
    () => filterCatalogProducts(products, config?.products),
    [products, config?.products],
  );
  const questions = useMemo(() => needsQuestions(available), [available]);
  const ranked = useMemo(() => rankNeedProducts(available, answers), [available, answers]);
  const leading = leadingNeedProduct(ranked);
  const answered =
    Number(!!answers.purpose) + Number(!!answers.shape) + Number(!!answers.scale);

  useEffect(() => {
    if (questions.length > 0) onWizardTotalChange(questions.length);
  }, [questions.length, onWizardTotalChange]);

  const atResults = questions.length > 0 && wizardIndex >= questions.length;
  const question = atResults ? null : questions[Math.min(wizardIndex, Math.max(questions.length - 1, 0))];

  const goNext = useCallback(() => {
    paneHasStepped.current = true;
    onWizardIndexChange(Math.min(wizardIndex + 1, questions.length));
  }, [onWizardIndexChange, wizardIndex, questions.length]);

  const selectChoice = (key: keyof NeedsAnswers, id: string, advance: boolean) => {
    onAnswersChange({ ...answers, [key]: id });
    if (!advance || atResults) return;
    if (advanceTimer.current != null) window.clearTimeout(advanceTimer.current);
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    advanceTimer.current = window.setTimeout(goNext, reduce ? 0 : 180);
  };

  const chooseProduct = (product: CatalogProduct) => {
    updateState(stateUpdatesForProduct(product));
    onChooseProduct(product);
  };

  const minStartingPriceFor = (partial: NeedsAnswers): number | undefined => {
    const prices = rankNeedProducts(available, partial)
      .map((item) => item.product.startingPrice)
      .filter((value): value is number => typeof value === 'number' && value > 0);
    return prices.length > 0 ? Math.min(...prices) : undefined;
  };

  const handleChoiceKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    choices: NeedChoice<string>[],
    key: keyof NeedsAnswers,
    idx: number,
  ) => {
    const total = choices.length;
    let nextIdx = idx;
    switch (event.key) {
      case ' ':
      case 'Enter':
        event.preventDefault();
        selectChoice(key, choices[idx].id, true);
        return;
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        nextIdx = (idx + 1) % total;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        nextIdx = (idx - 1 + total) % total;
        break;
      case 'Home':
        event.preventDefault();
        nextIdx = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIdx = total - 1;
        break;
      default:
        return;
    }
    const group = event.currentTarget.parentElement;
    const next = group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIdx];
    next?.focus();
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-[13px] text-ink-3">Produkte werden geladen …</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 max-w-lg mx-auto">
        <p className="text-[13px] font-medium text-rubric mb-3">Verbindungsfehler</p>
        <p className="text-ink">{error}</p>
        <button type="button" onClick={loadProducts} className="sv-btn sv-btn-ghost mt-6">
          Erneut laden
        </button>
      </div>
    );
  }

  if (available.length === 0) {
    return (
      <div className="py-16 max-w-lg mx-auto">
        <p className="text-[13px] text-ink-3 mb-3">Keine Produkte verfügbar</p>
        <p className="text-ink">
          Für diese Seite sind aktuell keine Produkte verfügbar. Bitte
          kontaktieren Sie uns direkt — wir helfen persönlich weiter.
        </p>
      </div>
    );
  }

  const stageKind = leading ? catalogProductKind(leading.product) : 'fixed';
  const stageDims = leading
    ? catalogPreviewDimensions(leading.product)
    : { width: 1200, length: 1200, height: 210 };
  const stagePhoto = leading?.product.imageUrl;

  const titleId = 'sv-need-title';
  const title = atResults
    ? ranked.length === 1
      ? 'Dazu passt'
      : 'Dazu passen'
    : question?.prompt ?? 'Sie haben ein Flachdach.';
  const lede = atResults
    ? 'Eine Empfehlung aus Ihren Angaben. Sie können jedes Fenster weiter konfigurieren.'
    : wizardIndex === 0
      ? 'Sie haben ein Flachdach. Eine Entscheidung jetzt — dann die nächste Frage.'
      : null;

  return (
    <section className="sv-need" aria-labelledby={titleId}>
      <div className="sv-need-layout">
        <div className="sv-need-questions">
          <div className="sv-need-progress" aria-hidden>
            {questions.map((item, idx) => {
              const isCurrent = !atResults && idx === wizardIndex;
              const isVisited = atResults || idx < wizardIndex;
              return (
                <span
                  key={item.key}
                  className={`sv-detent ${isCurrent ? 'is-active' : isVisited ? 'is-visited' : ''}`}
                />
              );
            })}
          </div>

          <div
            className={`sv-need-pane ${paneHasStepped.current ? 'is-stepping' : ''}`}
            key={atResults ? 'results' : question?.key}
          >
            <header className="sv-need-header">
              <h2 id={titleId} className="sv-need-title">
                {title}
              </h2>
              {lede && <p className="sv-lede mt-4">{lede}</p>}
            </header>

            {question && (
              <div
                role="radiogroup"
                aria-label={question.prompt}
                className="sv-need-choices"
              >
                {question.choices.map((choice, idx) => {
                  const selected = isChoiceSelected(answers, question.key, choice.id);
                  const choicePrice = minStartingPriceFor({
                    ...answers,
                    [question.key]: choice.id,
                  });
                  return (
                    <button
                      key={choice.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`sv-need-choice ${selected ? 'is-selected' : ''}`}
                      onClick={() => selectChoice(question.key, choice.id, true)}
                      onKeyDown={(event) =>
                        handleChoiceKeyDown(event, question.choices, question.key, idx)
                      }
                    >
                      <span
                        className={`sv-detent ${selected ? 'is-active' : ''}`}
                        aria-hidden
                      />
                      <span className="sv-need-choice-copy">
                        <span className="sv-need-choice-label">{choice.label}</span>
                        <span className="sv-need-choice-hint">{choice.hint}</span>
                      </span>
                      {choicePrice !== undefined && (
                        <span className="sv-need-choice-price">
                          ab {formatDisplayPrice(choicePrice, showGross, 0)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {atResults && (
              <ul className="sv-need-results-list">
                {ranked.map((item, idx) => {
                  const startingPrice =
                    typeof item.product.startingPrice === 'number' && item.product.startingPrice > 0
                      ? item.product.startingPrice
                      : undefined;
                  return (
                    <li key={item.product.id} style={{ animationDelay: `${idx * 50}ms` }}>
                      <button
                        type="button"
                        className={`sv-need-result ${idx === 0 ? 'is-lead' : ''}`}
                        onClick={() => chooseProduct(item.product)}
                      >
                        <span className="sv-need-result-copy">
                          <span className="sv-need-result-name">{item.product.name}</span>
                          <span className="sv-need-result-sub">
                            {catalogShortDescription(item.product)}
                          </span>
                          <span className="sv-need-result-reason">{item.reason}</span>
                        </span>
                        <span className="sv-need-result-meta">
                          {startingPrice !== undefined && (
                            <span className="sv-need-result-price">
                              ab {formatDisplayPrice(startingPrice, showGross, 0)}
                            </span>
                          )}
                          <span className="sv-need-result-cta">
                            Dieses Fenster konfigurieren
                            <span aria-hidden> →</span>
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <aside className="sv-need-visual" aria-live="polite">
          <div className="sv-need-stage" key={leading?.product.id ?? 'empty'}>
            <PerspectiveViewer
              kind={stageKind}
              productKey={leading?.product.id}
              dimensions={stageDims}
              selected
              size="lg"
              initialView="photo"
              photoUrl={stagePhoto}
            />
          </div>
          <p className="sv-need-caption">
            {leading && answered > 0 ? (
              <>
                <span className="sv-need-caption-name">{leading.product.name}</span>
                <span className="sv-need-caption-sep" aria-hidden>
                  ·
                </span>
                <span>{catalogShortDescription(leading.product)}</span>
              </>
            ) : (
              'Ein Oberlicht im Flachdach. Die nächste Antwort schärft, welches.'
            )}
          </p>
        </aside>
      </div>

      <div className="sv-need-skip">
        <button type="button" className="sv-btn sv-btn-ghost" onClick={onSkipToCatalog}>
          Bringt mich direkt zur Produktauswahl
        </button>
        <p className="sv-need-skip-note">Zum Beispiel für Architekten, die das Modell bereits kennen.</p>
      </div>
    </section>
  );
}
